/** The Communication lane's time axis (plan §1.1, phase 4) — pure: where every dot sits.
 *
 *  **Stages, aligned to the spine.** The axis is the spine's five stages side by side,
 *  each as wide as the spine gives it (equal bands), each with its own linear scale:
 *  Booked (the booking's first day), Pre-check-in (the wait), Departure (the 24 hours
 *  before it), On-site (start → end), After trip (end → the window's end, a week later:
 *  the survey). A trip of a day would be a sliver on one linear scale beside a month of
 *  waiting; per stage, the on-site day reads as clearly as the weeks before it. Without
 *  a start time the axis is one linear scale.
 *
 *  **Lanes** by kind: feed imports · changes (and console audit rows) · deliveries ·
 *  planned notifications (TBEL v2 only) · the trip itself (booked, start → end).
 *
 *  **A pending date/time change** (edit mode, the draft's date / departure time) moves
 *  the trip: the stored one stays as a dashed outline, the moved one is drawn where it
 *  goes, and every planned notification not yet sent is marked "will be replanned". The
 *  stage bands stay on the stored schedule, so nothing else shifts under the reader. */
import type {
  BookingTimeline,
  BookingTimelineItem,
  BookingTimelineSource,
  BookingTimelineSourceKey,
} from '@/api/bookingActions';
import { SANDBOX_STATUS_LABEL } from '@/features/delivery-logs/deliveryStatus';
import type { TimeMarkTone } from '@/features/records/timeline/marks';
import { axisBounds, axisX } from '@/features/records/timeline/scale';
import { formatZoned } from '@/utils/zonedTime';

import { SPINE_STAGES, type SpineStage } from '../registry';

import { STAGE_TITLES } from './spineModel';

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

export type AxisLane = 'feed' | 'changes' | 'sent' | 'planned' | 'trip';

export const AXIS_LANES: { lane: AxisLane; label: string; source: BookingTimelineSourceKey | null }[] = [
  { lane: 'feed', label: 'Feed', source: 'imports' },
  { lane: 'changes', label: 'Changes', source: 'changes' },
  { lane: 'sent', label: 'Sent', source: 'deliveries' },
  { lane: 'planned', label: 'Planned', source: 'planned' },
  { lane: 'trip', label: 'Trip', source: null },
];

const LANE_OF: Record<BookingTimelineItem['kind'], AxisLane> = {
  import: 'feed',
  change: 'changes',
  audit: 'changes',
  delivery: 'sent',
  planned: 'planned',
  booked: 'trip',
  start: 'trip',
  end: 'trip',
};

/** Planned states that have not fired yet — what a date move replans. */
const NOT_SENT = new Set(['Pending', 'Leased']);
const DONE = new Set(['Emitted', 'Completed']);
const DROPPED = new Set(['Skipped', 'Expired', 'Cancelled']);

/** Where the dot can take the reader (plan §1.1: "click a dot → the Activity/Integration
 *  tab row"); `audit` opens the audit log entry; null = details only. */
export type AxisTarget =
  | { tab: 'integration'; section: 'imports'; highlight: string }
  | { tab: 'activity'; section: 'deliveries'; highlight: string }
  | { tab: 'activity'; section: 'history'; highlight: null }
  | { tab: 'audit'; auditId: number };

export type DotTone = TimeMarkTone;

export interface AxisDot {
  key: string;
  item: BookingTimelineItem;
  lane: AxisLane;
  /** 0–1 across the plot. */
  x: number;
  /** "Delivery · tour_participant.created · Mon 14 Sep 18:25" — the dot's text label. */
  text: string;
  /** Second line of details (counts, states, who). */
  detail: string | null;
  tone: DotTone;
  target: AxisTarget | null;
}

export interface AxisBand {
  stage: SpineStage;
  title: string;
  /** 0–1 left edge. */
  x: number;
  width: number;
}

export interface AxisTick {
  x: number;
  label: string;
}

/** A source the axis cannot show in full, in the operator's words. */
export interface AxisGap {
  lane: AxisLane;
  /** 0–1: everything left of it is not loaded (truncated). */
  coveredX: number | null;
  text: string;
}

export interface AxisMove {
  /** The trip as stored and as it would move (0–1). */
  from: { x0: number; x1: number };
  to: { x0: number; x1: number };
  /** "Sat 3 Oct 09:30". */
  label: string;
}

export interface AxisModel {
  bands: AxisBand[];
  /** Under the real anchors: booked, start, end, the window's end. */
  ticks: AxisTick[];
  dots: AxisDot[];
  /** The stored trip (start → end), 0–1; null without a start. */
  trip: { x0: number; x1: number } | null;
  move: AxisMove | null;
  /** 0–1, or null when now is outside the axis. */
  now: number | null;
  gaps: AxisGap[];
  /** Planned rows are not listed for a TBEL v1 tenant (there is no computed history). */
  plannedOff: boolean;
  /** Each stage has its own scale (false: one linear scale, no start time). */
  staged: boolean;
}

const KIND_LABEL: Record<BookingTimelineItem['kind'], string> = {
  import: 'Import',
  change: 'Change',
  audit: 'Audit',
  delivery: 'Delivery',
  planned: 'Planned',
  booked: 'Booked',
  start: 'Departure',
  end: 'Trip end',
};

const ms = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

function detailOf(item: BookingTimelineItem, replan: boolean): string | null {
  if (item.import) return [item.import.origin, item.import.state, item.import.source_code].filter(Boolean).join(' · ');
  if (item.change) {
    const c = item.change;
    const paths = c.changed_paths.length ? c.changed_paths.join(', ') : c.action;
    return [`${c.entity === 'user' ? 'Guest' : 'Booking'} #${c.object_id}`, paths, c.actor_email ?? c.source, c.comment]
      .filter(Boolean)
      .join(' · ');
  }
  if (item.audit) {
    return [item.audit.event, item.audit.actor_email, item.audit.success ? null : 'failed'].filter(Boolean).join(' · ');
  }
  if (item.delivery) {
    const d = item.delivery;
    const n = `${d.n_notifications} ${d.n_notifications === 1 ? 'notification' : 'notifications'}`;
    return [d.status, n, d.n_error > 0 ? `${d.n_error} failed` : null].filter(Boolean).join(' · ');
  }
  if (item.planned) {
    const p = item.planned;
    return [p.state, `${p.scope} scope`, replan ? 'will be replanned' : null].filter(Boolean).join(' · ');
  }
  return null;
}

function toneOf(item: BookingTimelineItem, replan: boolean): DotTone {
  if (item.delivery && (item.delivery.n_error > 0 || /fail|error/i.test(item.delivery.status))) return 'error';
  // A sandbox kept everything in its outbox: nothing was sent, as expected (not a failure).
  if (item.delivery?.status === SANDBOX_STATUS_LABEL.captured) return 'dropped';
  if (item.audit && !item.audit.success) return 'error';
  if (item.planned) {
    if (replan) return 'replan';
    if (DONE.has(item.planned.state)) return 'done';
    if (DROPPED.has(item.planned.state)) return 'dropped';
    if (/error|fail/i.test(item.planned.state)) return 'error';
  }
  return 'default';
}

function targetOf(item: BookingTimelineItem): AxisTarget | null {
  if (item.import) return { tab: 'integration', section: 'imports', highlight: item.import.import_run_id };
  if (item.delivery) return { tab: 'activity', section: 'deliveries', highlight: item.delivery.delivery_id };
  if (item.change) return { tab: 'activity', section: 'history', highlight: null };
  if (item.audit) return { tab: 'audit', auditId: item.audit.audit_id };
  return null;
}

function gapText(lane: string, s: BookingTimelineSource, tz: string, mode?: string): string | null {
  const since = formatZoned(s.covered_from, tz, { weekday: false });
  if (s.timed_out) return `${lane}: the query timed out — complete only from ${since}.`;
  if (s.truncated) return `${lane}: complete only from ${since}${mode ?? ''}.`;
  return null;
}

/** Merges pages of the timeline (each paging further back) into one: items deduplicated,
 *  each source as complete as the oldest page says. */
export function mergeTimelinePages(pages: BookingTimeline[]): BookingTimeline | null {
  const first = pages[0];
  if (!first) return null;
  const last = pages[pages.length - 1]!;
  const seen = new Set<string>();
  const items: BookingTimelineItem[] = [];
  for (const p of pages) {
    for (const it of p.items) {
      const k = `${it.kind}:${it.id ?? it.ts}`;
      if (seen.has(k)) continue;
      seen.add(k);
      items.push(it);
    }
  }
  items.sort((a, b) => a.ts.localeCompare(b.ts));
  const merge = <S extends BookingTimelineSource>(key: BookingTimelineSourceKey): S => {
    const covered = pages.map((p) => p.sources[key].covered_from).sort()[0]!;
    return { ...(last.sources[key] as S), covered_from: covered, count: items.filter((i) => sourceOf(i) === key).length };
  };
  return {
    ...first,
    window: { from: last.window.from, to: first.window.to },
    items,
    sources: {
      imports: merge('imports'),
      changes: merge('changes'),
      audit: merge('audit'),
      deliveries: merge('deliveries'),
      planned: merge('planned'),
    },
  };
}

function sourceOf(i: BookingTimelineItem): BookingTimelineSourceKey | null {
  switch (i.kind) {
    case 'import':
      return 'imports';
    case 'change':
      return 'changes';
    case 'audit':
      return 'audit';
    case 'delivery':
      return 'deliveries';
    case 'planned':
      return 'planned';
    default:
      return null;
  }
}

/** Where to page back to: the newest `covered_from` of a truncated source — every source
 *  is complete from there on; null when nothing is missing. */
export function earlierWindow(t: BookingTimeline): string | null {
  const truncated = (Object.keys(t.sources) as BookingTimelineSourceKey[])
    .map((k) => t.sources[k])
    .filter((s) => s.truncated)
    .map((s) => s.covered_from)
    .sort();
  const to = truncated[truncated.length - 1];
  return to && to > t.window.from ? to : null;
}

export function timelineModel({
  timeline: t,
  pendingStart,
  now = new Date(),
}: {
  timeline: BookingTimeline;
  /** The draft's departure (edit mode), when it differs from the stored one. */
  pendingStart: number | null;
  now?: Date;
}): AxisModel {
  const tz = t.timezone;
  const start = ms(t.anchors.start_ts);
  const end = ms(t.anchors.end_ts);
  const duration = start !== null && end !== null ? Math.max(end - start, 60_000) : HOUR;
  const moved = pendingStart !== null && start !== null && pendingStart !== start ? pendingStart : null;
  const bounds = axisBounds(t, moved !== null ? moved + duration + HOUR : null);
  const linear: [number, number] = [ms(t.window.from) ?? 0, Math.max(ms(t.window.to) ?? 0, (ms(t.window.from) ?? 0) + DAY)];
  const x = (v: number) => axisX(v, bounds, linear);

  const bands: AxisBand[] = bounds
    ? SPINE_STAGES.map((stage, i) => ({ stage, title: STAGE_TITLES[stage], x: i / SPINE_STAGES.length, width: 1 / SPINE_STAGES.length }))
    : [];

  const ticks: AxisTick[] = [];
  const tick = (iso: string | null, opts: Parameters<typeof formatZoned>[2]) => {
    const v = ms(iso);
    if (v !== null) ticks.push({ x: x(v), label: formatZoned(v, tz, { zone: false, ...opts }) });
  };
  tick(t.anchors.booking_ts ?? t.anchors.created_ts, { time: false, weekday: false });
  if (bounds) {
    tick(t.anchors.start_ts, {});
    const sameDay = start !== null && end !== null && formatZoned(start, tz, { time: false }) === formatZoned(end, tz, { time: false });
    if (end !== null) ticks.push({ x: x(end), label: sameDay ? formatZoned(end, tz, { zone: false }).split(' ').pop()! : formatZoned(end, tz, { zone: false }) });
    ticks.push({ x: 1, label: formatZoned(bounds[5]!, tz, { time: false, weekday: false, zone: false }) });
  }

  const dots: AxisDot[] = t.items.map((item, i) => {
    const replan = moved !== null && Boolean(item.planned && NOT_SENT.has(item.planned.state));
    const when = formatZoned(item.ts, tz, { zone: false });
    const party = item.booking_id !== null && item.booking_id !== t.booking_id ? ` · booking #${item.booking_id}` : '';
    return {
      key: `${item.kind}:${item.id ?? item.ts}:${i}`,
      item,
      lane: LANE_OF[item.kind],
      x: x(new Date(item.ts).getTime()),
      text: `${KIND_LABEL[item.kind]} · ${item.label} · ${when}${party}${replan ? ' · will be replanned' : ''}`,
      detail: detailOf(item, replan),
      tone: toneOf(item, replan),
      target: targetOf(item),
    };
  });

  const trip = start !== null && end !== null ? { x0: x(start), x1: x(end) } : null;
  const move =
    moved !== null && trip
      ? {
          from: trip,
          to: { x0: x(moved), x1: x(moved + duration) },
          label: formatZoned(moved, tz, { zone: false }),
        }
      : null;

  const nowMs = now.getTime();
  const lo = bounds ? bounds[0]! : linear[0];
  const hi = bounds ? bounds[5]! : linear[1];
  const gaps: AxisGap[] = [];
  for (const { lane, label, source } of AXIS_LANES) {
    if (!source) continue;
    const s = t.sources[source];
    const text = gapText(label, s, tz, source === 'deliveries' ? ' (deliveries are read 31 days at a time)' : undefined);
    if (text) gaps.push({ lane, coveredX: s.truncated || s.timed_out ? x(new Date(s.covered_from).getTime()) : null, text });
  }
  // Audit rows share the Changes lane.
  const audit = gapText('Audit', t.sources.audit, tz);
  if (audit) gaps.push({ lane: 'changes', coveredX: null, text: audit });

  return {
    bands,
    ticks,
    dots,
    trip,
    move,
    now: nowMs >= lo && nowMs <= hi ? x(nowMs) : null,
    gaps,
    plannedOff: t.sources.planned.mode === 'off',
    staged: bounds !== null,
  };
}
